import { computed, ref, type Ref } from 'vue';
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router';
import { useI18n } from 'vue-i18n';

export interface BreadcrumbItemInput {
  label?: string;
  labelKey?: string; // Translation key for reactive translations
  to?: RouteLocationRaw;
  icon?: any;
}

export interface BreadcrumbItem {
  label: string;
  to?: RouteLocationRaw;
  icon?: any;
}

// Global breadcrumbs state - stores items with potential translation keys
const customBreadcrumbs: Ref<BreadcrumbItemInput[] | null> = ref(null);

export function useBreadcrumbs() {
  const route = useRoute();
  const router = useRouter();
  const { t } = useI18n();

  // Set custom breadcrumbs for a specific page
  // Items can have either 'label' (static) or 'labelKey' (translation key)
  const setBreadcrumbs = (items: BreadcrumbItemInput[]) => {
    customBreadcrumbs.value = items;
  };

  // Clear custom breadcrumbs (useful on component unmount)
  const clearBreadcrumbs = () => {
    customBreadcrumbs.value = null;
  };

  // Helper to resolve label from item (translates labelKey if present)
  const resolveLabel = (item: BreadcrumbItemInput): string => {
    if (item.labelKey) {
      return t(item.labelKey);
    }
    return item.label || '';
  };

  // Computed breadcrumbs that checks custom ones first, then falls back to route meta
  // This is reactive to locale changes because t() is called inside the computed
  const breadcrumbs = computed<BreadcrumbItem[]>(() => {
    // If custom breadcrumbs are set, use them
    if (customBreadcrumbs.value) {
      return customBreadcrumbs.value.map(item => ({
        label: resolveLabel(item),
        to: item.to,
        icon: item.icon,
      }));
    }

    // Otherwise, build from route meta
    const items: BreadcrumbItem[] = [];

    // Check if route has breadcrumbs defined in meta
    if (route.meta.breadcrumbs) {
      // If breadcrumbs is a function, call it with route params
      if (typeof route.meta.breadcrumbs === 'function') {
        return route.meta.breadcrumbs(route, t);
      }
      return route.meta.breadcrumbs as BreadcrumbItem[];
    }

    // Default fallback to current page title
    if (route.meta.title) {
      items.push({
        label: route.meta.title as string,
      });
    }

    return items;
  });

  return {
    breadcrumbs,
    setBreadcrumbs,
    clearBreadcrumbs,
  };
}
